import { Group, Paper, Text, Tooltip, ActionIcon, Progress } from "@mantine/core";
import {
  IconArrowBack,
  IconBell,
  IconEditCircle,
  IconEditCircleOff,
  IconLoader2,
  IconPower,
  IconRadar,
  IconRotate,
  IconShield,
  IconShieldOff,
} from "@tabler/icons-react";
import { useFetch } from "../../helpers/useFetch";
import { getMode } from "../../helpers/helpers";
import { toast } from "react-hot-toast";
import { openFormStartModal, openPcAlertModal, openPcBlockModal } from "../../helpers/modals";

function isPcOnline(computer) {
  if (!computer?.last_seen_at) return false;
  return Date.now() - new Date(computer.last_seen_at).getTime() < 6 * 60 * 1000;
}

export default function ComputerCard({ computer, onRefresh }) {
  const online = isPcOnline(computer);
  const status = computer?.status ?? null;
  const { mode } = getMode();

  const sendCommand = async (type, payload = null) => {
    const body = payload ? { type, payload } : { type };
    const res = await useFetch(`/pcapp/room/computer/${computer.id}/command`, "POST", body);
    if (res?.ok === false || res?.error) {
      toast.error(res?.message ?? "Příkaz se nepodařilo odeslat.");
    }
    onRefresh();
    return res;
  };

  const openAlert = () =>
    openPcAlertModal(async ({ title, message }) => {
      const res = await sendCommand("alert", { title, message });
      if (!(res?.ok === false || res?.error)) {
        toast.success("Alert odeslán.");
      }
    }, computer.hostname);

  const openBlock = () =>
    openPcBlockModal(async (durationSeconds) => {
      const res = await sendCommand("block", durationSeconds ? { durationSeconds } : null);
      if (!(res?.ok === false || res?.error)) {
        toast.success(durationSeconds ? "Počítač zablokován na omezenou dobu." : "Počítač zablokován.");
      }
    }, computer.hostname);

  const openFormsInit = () =>
    openFormStartModal(computer, async (childID) => {
      const res = await sendCommand("formsInit", { childID });
      if (!(res?.ok === false || res?.error)) {
        toast.success("Dotazník inicializován.");
      }
    });

  // Jakýkoli stav dotazníku. Pozor: appka hlásí stavy v jednotném čísle ("formInit", "formStart"),
  // zatímco příkazy jsou v množném ("formsInit"/"formsStart"). Bereme cokoli začínající na "form".
  const formsActive = online && typeof status === "string" && status.toLowerCase().startsWith("form");
  // Čeká na uvolnění (init) → nabídnout start i zrušení.
  const formsWaiting = formsActive && /init/i.test(status);
  // Vyplňování → jen zrušení.
  const formsFilling = formsActive && !formsWaiting;

  // Online počítač v nerozpoznaném stavu – ať karta není nikdy prázdná a jde dotazník ovládat.
  const knownStatus =
    ["ready", "blocked", "shuttingDown", "rebooting", "pinging", "error"].includes(status) || formsActive;
  const unknownState = online && !knownStatus;

  const progressColor = !online
    ? mode === "dark"
      ? "dark.6"
      : "gray.2"
    : status === "ready"
    ? "teal"
    : status === "blocked"
    ? "red"
    : status === "error"
    ? "red"
    : status === "shuttingDown" || status === "rebooting"
    ? "yellow"
    : status === "pinging"
    ? "pink"
    : formsActive
    ? "indigo"
    : mode === "dark"
    ? "dark.6"
    : "gray.2";

  const progressAnimated =
    online && (status === "shuttingDown" || status === "rebooting" || status === "pinging" || formsActive);

  return (
    <Paper className="select-none overflow-hidden" withBorder radius="md">
      <Progress color={progressColor} animated={progressAnimated} radius={0} value={100} className="w-full" />
      <div className="px-4 py-3 pb-4">
        <p className="text-xl font-bold">{computer.hostname}</p>
        {!online && (
          <Text size="sm" c="dimmed">
            Vypnuto
          </Text>
        )}
        {online && status === "ready" && (
          <Text size="sm" c="dimmed">
            Zapnuto
          </Text>
        )}
        {online && status === "blocked" && (
          <Text size="sm" c="dimmed">
            Zablokován
          </Text>
        )}
        {online && status === "shuttingDown" && (
          <Text size="sm" c="dimmed">
            Vypíná se
          </Text>
        )}
        {online && status === "rebooting" && (
          <Text size="sm" c="dimmed">
            Restartuje se
          </Text>
        )}
        {online && status === "pinging" && (
          <Text size="sm" c="dimmed">
            Pinguji...
          </Text>
        )}
        {formsWaiting && (
          <Text size="sm" c="dimmed">
            Dotazník čeká na uvolnění
          </Text>
        )}
        {formsFilling && (
          <Text size="sm" c="dimmed">
            Vyplňuje dotazník
          </Text>
        )}
        {online && status === "error" && (
          <Text size="sm" c="red">
            Chyba
          </Text>
        )}
        {unknownState && (
          <Text size="sm" c="dimmed">
            {status ?? "Neznámý stav"}
          </Text>
        )}

        <div className="mt-3 flex w-full justify-between gap-2">
          <Group gap="sm">
            {/* Ready state */}
            {online && status === "ready" && (
              <>
                <Tooltip label="Vypnout počítač" position="top" withArrow>
                  <ActionIcon variant="light" color="gray" size="lg" onClick={() => sendCommand("shutdownStart")}>
                    <IconPower size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Restartovat počítač" position="top" withArrow>
                  <ActionIcon variant="light" color="gray" size="lg" onClick={() => sendCommand("rebootStart")}>
                    <IconRotate size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Zablokovat počítač" position="top" withArrow>
                  <ActionIcon variant="light" color="gray" size="lg" onClick={openBlock}>
                    <IconShield size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Ping počítače" position="top" withArrow>
                  <ActionIcon variant="light" size="lg" color="gray" onClick={() => sendCommand("ping")}>
                    <IconRadar size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Spustit dotazník" position="top" withArrow>
                  <ActionIcon variant="light" size="lg" color="gray" onClick={openFormsInit}>
                    <IconEditCircle size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Odeslat alert" position="top" withArrow>
                  <ActionIcon variant="light" size="lg" color="gray" onClick={openAlert}>
                    <IconBell size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}

            {/* Offline state */}
            {!online && (
              <>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconPower size={24} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconRotate size={24} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconShield size={24} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconRadar size={24} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconEditCircle size={24} stroke={1.5} />
                </ActionIcon>
                <ActionIcon variant="light" size="lg" disabled>
                  <IconBell size={24} stroke={1.5} />
                </ActionIcon>
              </>
            )}

            {/* Shutting down */}
            {online && status === "shuttingDown" && (
              <Tooltip label="Zrušit vypnutí" position="top" withArrow>
                <ActionIcon variant="filled" color="yellow" size="lg" onClick={() => sendCommand("shutdownAbort")}>
                  <IconArrowBack size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Rebooting */}
            {online && status === "rebooting" && (
              <Tooltip label="Zrušit restart" position="top" withArrow>
                <ActionIcon variant="filled" color="yellow" size="lg" onClick={() => sendCommand("rebootAbort")}>
                  <IconArrowBack size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Blocked */}
            {online && status === "blocked" && (
              <Tooltip label="Odblokovat počítač" position="top" withArrow>
                <ActionIcon variant="filled" size="lg" color="red" onClick={() => sendCommand("unblock")}>
                  <IconShieldOff size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Pinging */}
            {online && status === "pinging" && (
              <ActionIcon variant="filled" color="pink" size="lg">
                <IconRadar size={24} stroke={1.5} />
              </ActionIcon>
            )}

            {/* Dotazník čeká na uvolnění (stav "formInit") → uvolnit + zrušit */}
            {formsWaiting && (
              <>
                <Tooltip label="Uvolnit dotazník" position="top" withArrow>
                  <ActionIcon variant="filled" size="lg" color="indigo" onClick={() => sendCommand("formsStart")}>
                    <IconEditCircle size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Zrušit dotazník" position="top" withArrow>
                  <ActionIcon variant="light" size="lg" color="indigo" onClick={() => sendCommand("formsAbort")}>
                    <IconEditCircleOff size={24} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}

            {/* Vyplňování dotazníku (stav "formStart") → jen zrušit */}
            {formsFilling && (
              <Tooltip label="Zrušit dotazník" position="top" withArrow>
                <ActionIcon variant="filled" size="lg" color="indigo" onClick={() => sendCommand("formsAbort")}>
                  <IconEditCircleOff size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Nerozpoznaný stav – vždy nabídni zrušení dotazníku jako únikovou akci */}
            {unknownState && (
              <Tooltip label="Zrušit dotazník" position="top" withArrow>
                <ActionIcon variant="light" size="lg" color="indigo" onClick={() => sendCommand("formsAbort")}>
                  <IconEditCircleOff size={24} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </div>
      </div>
    </Paper>
  );
}
